import React, { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import theme from "lib/material-ui/theme"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { SUCCESS } from "constants/index"
import { NAVIGATE_TO_PROFILE_PAGE } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import SuccessIcon from "components/atoms/_icons/SuccessIcon"
import ErrorIcon from "components/atoms/_icons/ErrorIcon"
import CTA from "components/atoms/CTA"

type PaymentResultProps = {
  status: PaymentResultStatus;
  reference: string;
}

export default function PaymentResult(props: PaymentResultProps) {
  const { status, reference } = props

  const orderDetail = useAppSelector((state) => state.order.orderDetail)

  const navigate = useNavigate()

  const isSuccess = useMemo(() => status.toLowerCase() === SUCCESS, [status])

  const { t } = useTranslation()

  function handleDiscard() {
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_PROFILE_PAGE))
  }

  function handleRetryPayment() {
    navigate(`/checkout/${orderDetail?.product?.id}`)
  }

  return (
    <Container>
      <Stack width="100%" justifyContent="center" alignItems="center" gap="20px">
        <IconContainer style={{ backgroundColor: isSuccess ? theme.palette.success[1] : theme.palette.error[1] }}>
          {isSuccess ? <SuccessIcon/> : <ErrorIcon/>}
        </IconContainer>
        <Stack alignItems="center" gap="10px" marginBottom="10px">
          {status && <Message>
            {t(`payment.message_${status.toLowerCase()}`)}
          </Message>}
          {reference && <ReferenceCode>
            {t(`payment.referenceCode`)}: <span className="farsi-digits">{reference}</span>
          </ReferenceCode>}
        </Stack>
        <ButtonsWrapper>
          {isSuccess ? <CTA
            variant="contained"
            onClick={handleDiscard}
          >
            {t("general.back")}
          </CTA> : <>
            <CTA
              variant="contained"
              onClick={handleRetryPayment}
            >
              {t("payment.retry")}
            </CTA>
            <CTA
              variant="outlined"
              onClick={handleDiscard}
            >
              {t("general.discard")}
            </CTA>
          </>}
        </ButtonsWrapper>
      </Stack>
    </Container>
  )
}

const Container = styled(Stack)(() => ({
  width: "100%",
  maxWidth: "var(--max-container-width)",
  height: "calc(100vh - 48px - 38px)",
  justifyContent: "center",
  alignItems: "center"
}))

const IconContainer = styled(Stack)(({ theme }) => ({
  width: "100px",
  height: "100px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",

  "& svg": {
    width: "64px",
    height: "64px"
  }
}))

const Message = styled(Typography)(({ theme }) => ({
  fontSize: "1.8rem",
  fontWeight: 700,
  color: theme.palette.gray[15]
}))

const ReferenceCode = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  color: theme.palette.gray[15]
}))

const ButtonsWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  maxWidth: "280px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px"
}))
